import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TICKETS_DEFAULT_SORT_COLUMN, TICKETS_DEFAULT_SORT_COLUMN_DIRECTION, TICKETS_PAGE_DEFAULT_SIZE, TICKETS_PAGE_SIZE_OPTIONS} from 'src/app/conf/support';
import { Ticket } from 'src/app/model/ticket';
import { AppState } from 'src/app/store/app.reducer';
import * as TicketActions from 'src/app/store/ticket/ticket.actions';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import * as HomeActions from 'src/app/store/home/home.actions';
import { selectTicketsLoading, selectTickets, selectTicketsPage, selectAddSuccessTicket, selectCloseSuccessTicket } from 'src/app/store/ticket/ticket.selector';
import { ConfirmationService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TicketDetailsDialogComponent } from '../ticket-details-dialog/ticket-details.dialog.component';
import { TicketDialogComponent } from '../tickets-dialog/ticket.dialog.component';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-tickets-list',
	templateUrl: './tickets-list.component.html',
	providers: [ConfirmationService, DialogService]
})

export class TicketsListComponent implements OnInit, OnDestroy {
	// Observables and subscriptions
	ticketsLoading$: Observable<boolean | undefined>;
	ticketsLoadingSub: Subscription | undefined;
	tickets$: Observable<Ticket[] | undefined>;
	ticketsSub: Subscription | undefined;
	ticketsPage$: Observable<ItemsPagination | undefined>;
	ticketsPageSub: Subscription | undefined;
	addTicket$: Observable<boolean | undefined>;
	addTicketSub: Subscription | undefined;
	closeTicket$: Observable<boolean | undefined>;
	closeTicketSub: Subscription | undefined;

	tickets: Ticket[];
	ticketsLoading: boolean;

	paging: PaginationParams = {
		size: TICKETS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: TICKETS_DEFAULT_SORT_COLUMN,
		order: TICKETS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	totalRecords = 0;
	pageSizeOptions = TICKETS_PAGE_SIZE_OPTIONS;
	pageSize = TICKETS_PAGE_DEFAULT_SIZE;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	screenWidth: number;

	filterSub: Subscription;
	searchFilter: FormControl = new FormControl('');
	filter: '';

	ref: DynamicDialogRef | undefined;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.store.dispatch(TicketActions.getTickets({ paging: this.paging, filter: this.filter }));
		this.subscribeAll();
		
		this.onResize();
	}

	ngOnDestroy(): void {
		this.ticketsLoadingSub?.unsubscribe();
		this.ticketsSub?.unsubscribe();
		this.ticketsPageSub?.unsubscribe();
		this.filterSub?.unsubscribe();
		this.addTicketSub?.unsubscribe();
		this.closeTicketSub?.unsubscribe();
	}

	initSelectors(): void{
		// Store selectors
		this.ticketsLoading$ = this.store.select(selectTicketsLoading);
		this.tickets$ = this.store.select(selectTickets);
		this.ticketsPage$ = this.store.select(selectTicketsPage);
		this.addTicket$ = this.store.select(selectAddSuccessTicket);
		this.closeTicket$ = this.store.select(selectCloseSuccessTicket);
	}

	subscribeAll(): void {
		this.ticketsLoadingSub = this.ticketsLoading$?.subscribe(
			(ticketsLoading) => {
				this.ticketsLoading = ticketsLoading;
			}
		);
		this.ticketsSub = this.tickets$?.subscribe(
			(tickets) => {
				this.tickets = tickets;
			}
		);
		this.ticketsPageSub= this.ticketsPage$?.pipe(filter(p => !!p))
		.subscribe(
				paging => {
					if (paging !== null && paging !== undefined){
						this.totalRecords = paging.TotalCount;
					} else {
						this.totalRecords = 0;
					}
				}
		);
		this.filterSub = this.searchFilter.valueChanges.pipe(
				debounceTime(500),
				map(v => v.trim().toLowerCase())
		).subscribe(value => {
				this.filter = value;
				this.store.dispatch(TicketActions.getTickets({ paging: this.paging, filter: value }));
		});
		this.addTicketSub = this.addTicket$?.subscribe(
			(addSuccess) => {
				if (addSuccess) {
					this.store.dispatch(TicketActions.getTickets({ paging: this.paging, filter: this.filter }));
			}
			}
		);
		this.closeTicketSub = this.closeTicket$?.subscribe(
			(closeSuccess) => {
				if (closeSuccess) {
					this.store.dispatch(TicketActions.getTickets({ paging: this.paging, filter: this.filter }));
			}
			}
		);
	}

	switchToTechnicians(): void {
		this.store.dispatch(HomeActions.selectSupportTab({index: 1}));
	}

	loadTickets(event: TableLazyLoadEvent) {
		this.ticketsLoading = true;

		this.paging = pageSortChange(this.paging, event);

		this.store.dispatch(TicketActions.getTickets({ paging: this.paging, filter: this.filter }));
	}

	addSupportRequestTicket(): void {
		this.store.dispatch(TicketActions.startAddTicket());
		this.ref = this.dialogService.open(TicketDialogComponent, {
			header: this.translate.instant('ADD_TICKET'),
			width: '70vw',
			breakpoints: { '1600px' : '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	infoTicket(ticket: Ticket): void {
		this.ref = this.dialogService.open(TicketDetailsDialogComponent, {
			header: this.translate.instant('SUPPORT_REQUEST'),
			width: '60vw',
			breakpoints: { '1600px' : '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				ticket
			},
		});
	}

	closeTicket(ticketClose: Ticket): void {
		this.store.dispatch(TicketActions.startCloseTicket());
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('ARE_YOU_SURE'),
			message: this.translate.instant('CLOSE_TICKET'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				const ticket: Ticket = {
					...ticketClose,
					ClosingNote: null
				};
				this.store.dispatch(TicketActions.closeTicket({ ticket }));
			},
			reject: () => {
			}
		});
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	col12Responsive(): string {
		return StandardPageLayout.col6ResponsiveNoPadding(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	dynamicInputTextWidth() : string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth,'w-25rem');
	}

}
